import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { usePageTitle } from '@/features/pageTitle';
import { Box, PageContainer } from '@/shared/ui';
import type { TTab } from '@/shared/ui/Tabs';
import { Tabs } from '@/shared/ui/Tabs';
import { getRoutePath } from '@/shared/utils/routes';

import { ChannelSettingsTab } from './ChannelSettingsTab';
import { ChannelsLogsTab } from './ChannelsLogsTab';
import { SubscriptionsTab } from './SubscriptionsTab';

type TabValue = 'settings' | 'logs' | 'subscriptions';

export const ChannelSettingsPage = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const initialPreviousPath = useRef(
    location.state?.from || getRoutePath('MAIN', 'CHANNELS')
  );

  usePageTitle(t('channels:settings.title'), true, initialPreviousPath.current);
  const getActiveTab = useCallback((): TabValue => {
    if (tab && ['settings', 'logs', 'subscriptions'].includes(tab)) {
      return tab as TabValue;
    }
    return 'settings';
  }, [tab]);

  const [activeTab, setActiveTab] = useState<TabValue>(getActiveTab());

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [tab, getActiveTab]);

  const handleTabChange = (newValue: TabValue) => {
    setActiveTab(newValue);
    navigate(`/channels/${id}/info/${newValue}`, {
      state: {
        from: `/channels/${id}/info/${newValue}`,
        isFromCompanies: location.state?.isFromCompanies,
      },
    });
  };

  const tabs: TTab[] = [
    {
      value: 'settings',
      label: t('channels:settings.tabs.settings'),
      content: <ChannelSettingsTab channelId={id!} />,
    },
    {
      value: 'logs',
      label: t('channels:settings.tabs.logs'),
      content: <ChannelsLogsTab channelId={id} />,
    },
    {
      value: 'subscriptions',
      label: t('channels:settings.tabs.subscriptions'),
      content: (
        <>
          <h1>{t('channels:settings.tabs.subscriptions')}</h1>
          <SubscriptionsTab channelId={id!} />
        </>
      ),
    },
  ];

  return (
    <PageContainer>
      <Box>
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={(_, newValue) => handleTabChange(newValue as TabValue)}
        />
      </Box>
    </PageContainer>
  );
};
